import * as React from 'react';
import { jsx, css } from '@emotion/react';
import { setTime, wait } from 'Src/me/pages/guest/proposals/new/utils';
import { mediaQueries, autoResponsiveClamp } from 'Src/me/pages/guest/proposals/new/React/viewHelpers';
import type { FeaturePropsType } from '../../../../../chat/views/types';
import type { RenderBuildingTypeButtonGroupPropsType } from '../../../../../chat/views/BuildingTypeButtonGroup/index';
import { scrollToBottomOfChat } from '../../../../../chat/functional/scrollToBottomOfChat';
import { BuildingTypeAnswerButton } from './components/BuildingTypeButton';
import { BuildingTypeAnswerCheckboxLikeButton } from './components/BuildingTypeRadioLikeButton';
import { useCurrentDevice } from '../../../../hooks/useCurrentDevice';

const { tablet, mobile } = mediaQueries();

export type BuildingTypeButtonGroupPropsType = FeaturePropsType<RenderBuildingTypeButtonGroupPropsType>

export const BuildingTypeButtonGroup = (props: BuildingTypeButtonGroupPropsType) => {
  const {
    keyName,
    isInstant,
    doesNotScroll,
    enableHideAnimation: enableHideAnimationFromProps = true,
    shouldEnableHideAnimation,
    continueHandler,
    answers,
  } = props;
  const [isAnswered, setIsAnswered] = React.useState(false);
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [showAnswerPanel, setShowAnswerPanel] = React.useState(true);
  const [isOtherAnswerSelected, setIsOtherAnswerSelected] = React.useState(false);
  const [isShow, setIsShow] = React.useState(isInstant);
  const [enableHideAnimation, setEnableHideAnimation] = React.useState(enableHideAnimationFromProps);
  const { isMobileView } = useCurrentDevice();

  const initialDisplayAnswers = [
    answers.find((answer) => answer.id === 1),
    answers.find((answer) => answer.id === 0),
  ];
  const otherAnswers = answers.filter((answer) => !initialDisplayAnswers.map((a) => a.id).includes(answer.id));

  React.useEffect(() => {
    (async () => {
      if (!isInstant) {
        await wait(0.4);

        setIsShow(true);
      }
    })();
  }, [isInstant]);

  React.useEffect(() => {
    if (!doesNotScroll) scrollToBottomOfChat();
  }, [doesNotScroll]);

  return (
    <div
      css={css`
        display: grid;
        grid-template-rows: minmax(0, 1fr);
        transition: grid-template-rows ${setTime(0.4)}s;

        ${!showAnswerPanel && css`
          overflow: hidden;
          grid-template-rows: minmax(0, 0fr);

          ${!enableHideAnimation && css`
            display: none;
          `}
        `}
      `}
    >
      <div
        css={css`
          padding: 30px 12px;
          position: relative;
          opacity: 0;
          bottom: -10px;
          transition: opacity ${setTime(0.4)}s ${setTime(0.4)}s, bottom ${setTime(0.4)}s ${setTime(0.4)}s;

          ${isShow && css`
            opacity: 1;
            bottom: 0;
          `}
        `}
      >
        <div
          css={css`
            max-width: 626px;
            margin-inline: auto;
            padding: 20px 18px;
            background-color: #bae8e8;

            ${mobile(({ clamp }) => css`
              padding: ${clamp('8px', '12px')};
              border-radius: 4px;
            `)}

            ${isOtherAnswerSelected && isMobileView && css`
              ${mobile(({ clamp }) => css`
                padding: ${clamp('8px', '10px')};
              `)}
            `}
          `}
        >
          <div
            css={css`
              ${isOtherAnswerSelected && isMobileView && css`
                display: none;
              `}
            `}
          >
            <div
              css={css`
                display: grid;
                grid-template-columns: 1.66fr 1fr;
                gap: 10px 16px;

                ${tablet(({ clamp }) => css`
                  grid-template-columns: 2fr 1fr;
                `)}

                ${mobile(({ clamp }) => css`
                  grid-template-columns: 1.8fr 1fr;
                  gap: 10px ${clamp('10px', '16px')};
                `)}
              `}
            >
              {initialDisplayAnswers.map((answer) => {
                const { id, answerHandler } = answer;
                const onClickHandler = async (event: React.MouseEvent<HTMLButtonElement>) => {
                  const { value: currentValue, textContent } = event.currentTarget;

                  if (currentValue === '0') {
                    setIsExpanded(true);
                    setIsOtherAnswerSelected(true);
                  } else {
                    // 回答済みフラグを立てる
                    setIsAnswered(true);

                    // 個別の回答処理を実行
                    answerHandler(event);

                    // 回答によって非表示のアニメーションを無効にする処理
                    if (shouldEnableHideAnimation) {
                      setEnableHideAnimation(shouldEnableHideAnimation(textContent));
                    }

                    await wait(0.5);

                    // 回答パネルを非表示にする
                    setShowAnswerPanel(false);

                    if (enableHideAnimation) await wait(0.5);

                    // 回答内容を渡して次の処理に進む
                    continueHandler({
                      value: currentValue,
                      answerText: textContent,
                      selfDomReferences: [],
                    });
                  }
                };

                return (
                  <BuildingTypeAnswerButton
                    key={id}
                    {...props}
                    {...answer}
                    isAnswered={isAnswered}
                    onClickHandler={onClickHandler}
                  />
                );
              })}
            </div>
            <div
              css={css`
                display: ${isExpanded ? 'block' : 'none'};
                padding-top: 24px;
              `}
            >
              <p
                css={css`
                  color: #000;
                  font-size: 16px;
                  font-weight: bold;
                  margin-bottom: 8px;

                  ${mobile(({ clamp }) => css`
                    font-size: ${clamp('13px', '16px')};
                  `)}
                `}
              >
                以下から当てはまるものを選択してください
              </p>
              <div
                css={css`
                  display: grid;
                  grid-template-columns: 1fr 1fr 1fr;
                  gap: 8px 10px;

                  ${mobile(() => css`
                    grid-template-columns: 1fr 1fr;
                  `)}
                `}
              >
                {otherAnswers.map((answer) => {
                  const { id, answerHandler } = answer;
                  const onClickHandler = async (event: React.MouseEvent<HTMLButtonElement>) => {
                    const { value: currentValue, textContent } = event.currentTarget;

                    // 回答済みフラグを立てる
                    setIsAnswered(true);

                    // 個別の回答処理を実行
                    answerHandler(event);

                    // 回答によって非表示のアニメーションを無効にする処理
                    if (shouldEnableHideAnimation) {
                      setEnableHideAnimation(shouldEnableHideAnimation(textContent));
                    }

                    await wait(0.5);

                    // 回答パネルを非表示にする
                    setShowAnswerPanel(false);

                    if (enableHideAnimation) await wait(0.5);

                    // 回答内容を渡して次の処理に進む
                    continueHandler({
                      value: currentValue,
                      answerText: textContent,
                      selfDomReferences: [],
                    });
                  };

                  return (
                    <BuildingTypeAnswerCheckboxLikeButton
                      key={id}
                      {...props}
                      {...answer}
                      isAnswered={isAnswered}
                      onClickHandler={onClickHandler}
                    />
                  );
                })}
              </div>
            </div>
          </div>
          <div
            css={css`
              display: none;

              ${isOtherAnswerSelected && isMobileView && css`
                display: block;
              `}
            `}
          >
            <select
              name={keyName}
              onChange={async (event) => {
                const { value: currentValue } = event.currentTarget;
                const selectedOption = event.currentTarget.querySelector('option:checked');

                // 回答済みフラグを立てる
                setIsAnswered(true);

                // 個別の回答処理を実行
                answers.find((answer) => answer.id === 1).answerHandler(event);

                await wait(0.5);

                // 回答パネルを非表示にする
                setShowAnswerPanel(false);

                // 回答内容を渡して次の処理に進む
                continueHandler({
                  value: currentValue,
                  answerText: selectedOption.textContent,
                  selfDomReferences: [],
                });
              }}
              css={css`
                color: #041156;
                font-size: 18px;
                width: 100%;
                height: calc(1lh + 13px);
                background: #fff;
                border: 1px solid #041156;

                ${mobile(({ clamp }) => css`
                  font-size: ${clamp('16px', '18px')};
                `)}
              `}
            >
              <option>選択してください</option>
              {otherAnswers.map((answer) => {
                const { id, value, textContents } = answer;

                return (
                  <option key={id} value={value}>{textContents}</option>
                );
              })}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};
